import { useRef, useState, type FormEvent } from 'react'

import { Icon } from './Icon'
import { Reassurance } from './Cta'
import { submitPartnershipEnquiry } from '../lib/submissionApi'

type FormStatus =
  | 'idle'
  | 'submitting'
  | 'success'
  | 'error'
  | 'validation'
  | 'unconfigured'

const endpoint = import.meta.env.VITE_CONTACT_ENDPOINT?.trim()
const netlifyFallbackEnabled = import.meta.env.VITE_NETLIFY_FORMS_ENABLED === 'true'
const netlifyFormName = 'talent-enquiry'

const statusMessages: Record<FormStatus, string> = {
  idle: 'Your information will only be used to respond to this enquiry.',
  submitting: 'Sending your enquiry securely…',
  success: 'Thank you. Your enquiry has been sent and we will be in touch soon.',
  error: 'We could not send your enquiry. Please try again or email partnerships@arksolutionsza.com.',
  validation: 'Please complete all three fields before submitting your enquiry.',
  unconfigured:
    'Secure online delivery is being connected. Please use the partnership email below in the meantime.',
}

export function Contact() {
  const [status, setStatus] = useState<FormStatus>('idle')
  const idempotencyKeyRef = useRef<string | undefined>(undefined)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    if (String(formData.get('bot-field') ?? '').trim()) {
      form.reset()
      setStatus('success')
      return
    }

    if (!form.checkValidity()) {
      form.reportValidity()
      setStatus('validation')
      return
    }

    if (!endpoint && !netlifyFallbackEnabled) {
      setStatus('unconfigured')
      return
    }

    setStatus('submitting')
    const payload = {
      email: String(formData.get('email') ?? ''),
      firm: String(formData.get('firm') ?? ''),
      need: String(formData.get('need') ?? ''),
    }

    try {
      idempotencyKeyRef.current ??= crypto.randomUUID()
      if (endpoint) {
        await submitPartnershipEnquiry(payload, idempotencyKeyRef.current)
      } else if (netlifyFallbackEnabled) {
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ 'form-name': netlifyFormName, ...payload }).toString(),
        })
        if (!response.ok) throw new Error(`Enquiry failed with ${response.status}`)
      }

      form.reset()
      idempotencyKeyRef.current = undefined
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  const noteClass =
    status === 'success'
      ? 'form-note is-success'
      : status === 'error' || status === 'validation'
        ? 'form-note is-error'
        : status === 'unconfigured'
          ? 'form-note is-setup'
          : 'form-note'

  return (
    <section
      className="section--dark conversation"
      id="conversation"
      aria-labelledby="conversation-title"
    >
      <svg
        className="conversation-arc"
        viewBox="0 0 1600 700"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M-60 500C280 73 810 53 1680 450"
          fill="none"
          stroke="var(--blue)"
          strokeWidth="5"
        />
        <circle cx="170" cy="282" r="13" fill="#ff6853" />
        <circle cx="1410" cy="345" r="13" fill="#fff" />
      </svg>
      <div className="container conversation-grid">
        <div className="reveal">
          <p className="section-number">09 / Start here</p>
          <h2 id="conversation-title">Start with a conversation.</h2>
          <p className="conversation-copy">
            Tell us about the skills or capacity you may need. There is no charge
            and no obligation to interview or hire anyone.
          </p>
          <Reassurance />
        </div>
        <form
          className="conversation-form reveal"
          name={netlifyFormName}
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          noValidate
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form-name" value={netlifyFormName} />
          <p hidden>
            <label>
              Do not fill this out if you are human:
              <input name="bot-field" tabIndex={-1} autoComplete="off" />
            </label>
          </p>
          <div className="field">
            <label htmlFor="email">Work email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="name@firm.com"
              maxLength={254}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="firm">Firm name</label>
            <input
              id="firm"
              name="firm"
              type="text"
              autoComplete="organization"
              placeholder="Your firm name"
              maxLength={200}
              required
            />
          </div>
          <div className="field field--wide">
            <label htmlFor="need">What do you need?</label>
            <textarea
              id="need"
              name="need"
              placeholder="Tell us about the skills or capacity you may need..."
              maxLength={4000}
              required
            />
          </div>
          <button
            className="button button--full field--wide"
            type="submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Sending enquiry' : 'Submit enquiry'}
            <Icon name="arrow" />
          </button>
          <p className={noteClass} aria-live="polite">
            {statusMessages[status]}
            {status === 'unconfigured' && (
              <> <a href="mailto:partnerships@arksolutionsza.com">partnerships@arksolutionsza.com</a></>
            )}
          </p>
        </form>
      </div>
    </section>
  )
}
